import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Progress } from 'reactstrap'

// Import steps in order of execution
const IMPORT_STEPS = [
  'refreshing_source_code',
  'parsing_translation_files',
  'detecting_locales',
  'detecting_string_locales',
  'updating_existing_strings',
  'destroying_removed_strings',
  'creating_strings',
  'creating_strings_with_unknown_locale',
  'importing_values_from_other_locales',
  'detecting_unused_strings',
  'detecting_duplicated_strings'
]

// Steps that have granular progress tracking
const STEPS_WITH_PROGRESS = [
  'detecting_string_locales',
  'updating_existing_strings',
  'destroying_removed_strings',
  'creating_strings',
  'detecting_unused_strings',
  'detecting_duplicated_strings'
]

const SynchronizationStatus = ({ importProgress = 0, importStep = null }) => {
  const { t } = useTranslation('ProjectsPage')

  const stepIndex = IMPORT_STEPS.indexOf(importStep)
  const hasGranularProgress = STEPS_WITH_PROGRESS.includes(importStep)

  // Calculate combined progress:
  // Each step contributes ~10% (100/10 steps)
  // Within a step with granular progress, interpolate
  const stepBaseProgress = stepIndex >= 0
    ? Math.round((stepIndex / IMPORT_STEPS.length) * 100)
    : 0
  const stepContribution = 100 / IMPORT_STEPS.length

  const calculateProgress = () => {
    // For steps with granular progress, always use granular calculation
    // This shows progress from start to end of the step (e.g., 30% to 40%)
    if (hasGranularProgress) {
      return Math.round(stepBaseProgress + ((importProgress * stepContribution) / 100))
    }
    // For steps without granular progress, show the end of the step
    if (stepIndex >= 0) {
      return Math.round(((stepIndex + 1) / IMPORT_STEPS.length) * 100)
    }
    return 0
  }

  const progress = calculateProgress()

  return (
    <div className="mt-3 text-center" style={{ width: '100%' }}>
      {importStep && (
        <>
          <small className="text-muted d-block mb-2">
            {t(`ProjectCard.SynchronizationStatus.${importStep}`)}
            {hasGranularProgress && importProgress > 0 && (
              <span className="ms-1">
                (
                {importProgress}
                %)
              </span>
            )}
          </small>
          <Progress
            value={progress}
            className="mx-3"
            style={{ height: '6px' }}
            animated
            striped
          />
          <small className="text-muted mt-1 d-block">
            {`${progress}%`}
          </small>
        </>
      )}
    </div>
  )
}

SynchronizationStatus.propTypes = {
  importProgress: PropTypes.number,
  importStep: PropTypes.string
}

export default SynchronizationStatus
